// A component's own limitations — the pure half of the Aircraft Item Details tab's
// add / edit (component editor, 2026-10-07). The API is POST / PATCH / DELETE
// /v6/items/{id}/limitations (app/items/limitations.py).
//
// Legacy (intent): mod_parts/includes/tabs/parts_tab_item_aircraft.inc:598-660 posts
// save_limitation (parts_ajax_handler.inc:2090-2229).
//   @Install TSN -> total_life_used (TSN AT INSTALL; "UNK" = -1, :620)
//   @Install TSO -> life_at_install
// The Total TSN / TSO the grid shows are computed by the API and are never sent back.
//
// 2026-10-14, finishing the port (Aaron: "Components edit never got completed"), from the
// aircraft tab's Edit Limitation modal (mod_aircraft/includes/aircraft_component_edit.inc, "EDIT"):
//   Based On      the tenant's aircraft_based_on_list (ACAPI:2093-2112), not a fixed list: the old
//                 list spelt "Engine 1 ng Cycles" / "Engine 1 Start", which no counter resolves
//   Type          a stored type outside the tenant's list stays on the select (Mustang: 417 rows)
//   Limited Life  (aircraft_limitation_type_no_period) Period = Finite Life, TSO = TSN (EDIT:583-614)
//   Overhaul      (aircraft_limitation_type_no_life) no Finite Life — "N/A" (EDIT:597-603)
//   Warning Unit  defaults from Based On (aircraft.inc:2310-2318)
//   Position, Reference (EDIT:213-233, :797-802)
import { BASED_ON_OPTIONS } from "../../utils/limitationVocab";

/** A row of GET /v6/items/{id}/limitations (app/items/schemas.py ItemLimitationList). */
export interface ComponentLimitationRow {
  id: number;
  item_id: number;
  based_on: string | null;
  limitation_type: string | null;
  life: string | number | null;
  life_hours: string | number | null;
  warning: string | number | null;
  warning_unit: string | null;
  total_life_used: string | number | null;
  life_at_install: string | number | null;
  aircraft_install: string | number | null;
  aircraft_id: number | null;
  position: string | null;
  reference?: string | null;
  install_date: string | null;
  expiry_date: string | null;
  limitation_notes: string | null;
  units_remaining: string | number | null;
  used_this_install: string | number | null;
  total_tsn: string | number | null;
  total_tso: string | number | null;
  next_due: string | null;
  remaining_units: string | number | null;
  remaining_days: number | null;
}

export interface LimitationFormValues {
  based_on: string;
  limitation_type: string;
  life: string;
  life_hours: string;
  warning: string;
  warning_unit: string;
  tsn: string;
  tsn_unknown: boolean;
  tso: string;
  aircraft_install: string;
  /** "YYYY-MM-DD" or "" (TenantDatePicker's wire form). */
  install_date: string;
  expiry_date: string;
  limitation_notes: string;
  time_to_run: string;
  reference: string;
  position: string;
}

/** Legacy's default Type list when the tenant sets no aircraft_component_type_list
 * (parts_tab_item_aircraft.inc:12-19). */
export const DEFAULT_COMPONENT_TYPES: ReadonlyArray<{ value: string; label: string }> = [
  { value: "Overhaul", label: "Overhaul" },
  { value: "Limited Life", label: "Limited Life" },
  { value: "On Condition", label: "On Condition" },
  { value: "Not Applicable", label: "Not Applicable" },
  { value: "Retire", label: "Retire" },
  { value: "Servicing", label: "Servicing" },
];

/** aircraft_component_type_list is "value:name,value:name" in the config row
 * (parts_tab_item_aircraft.inc:4-10); a bare "value" is its own label. Blank or missing →
 * legacy's defaults.
 *
 * useConfig("aircraft") does NOT serve that string: /v6/config turns a "k:v,k:v" value into an
 * OBJECT ({"Overhaul": "Overhaul", …}), any other comma list into an ARRAY, and leaves a single
 * member a STRING (Aeronet.app app/config/schemas.py validate_value_dict / validate_value_list).
 * g2 and Mustang hold the "k:v" form, so the sheet got an object and `.split` threw the moment
 * the config arrived (2026-10-14). Every served shape is read here. */
export function componentTypeOptions(raw?: unknown): Array<{ value: string; label: string }> {
  let parts: string[] = [];
  if (typeof raw === "string") parts = raw.split(",");
  else if (Array.isArray(raw)) parts = raw.map(String);
  else if (raw && typeof raw === "object") {
    parts = Object.entries(raw as Record<string, unknown>).map(([k, v]) => `${k}:${v ?? ""}`);
  }
  const parsed = parts
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const [value, ...rest] = part.split(":");
      const label = rest.join(":").trim();
      return { value: value.trim(), label: label || value.trim() };
    })
    .filter((o) => o.value);
  return parsed.length ? parsed : [...DEFAULT_COMPONENT_TYPES];
}

export const isDateAxis = (basedOn?: string | null) => (basedOn ?? "").includes("Date");
export const isConditionAxis = (basedOn?: string | null) =>
  ["On Condition", "Not Applicable"].includes((basedOn ?? "").trim());

const str = (v: unknown) => (v === null || v === undefined ? "" : String(v));
/** The wire's "YYYY-MM-DD" day, "" for none or legacy's zero-date. */
const isoDay = (raw?: string | null) => {
  const day = String(raw ?? "").slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) && !day.startsWith("0000") ? day : "";
};

export function emptyLimitationForm(): LimitationFormValues {
  return {
    based_on: "",
    limitation_type: "",
    life: "",
    life_hours: "",
    warning: "",
    warning_unit: "",
    tsn: "",
    tsn_unknown: false,
    tso: "",
    aircraft_install: "",
    install_date: "",
    expiry_date: "",
    limitation_notes: "",
    time_to_run: "",
    reference: "",
    position: "",
  };
}

/** The edit form seeded from a row. TSN is the AT-INSTALL figure (total_life_used), never
 * the computed Total TSN — seeding the total is how a re-save double-counts. */
export function limitationFormValues(row: ComponentLimitationRow): LimitationFormValues {
  const unknown = Number(row.total_life_used) === -1;
  return {
    based_on: str(row.based_on),
    limitation_type: str(row.limitation_type),
    life: str(row.life),
    life_hours: str(row.life_hours),
    warning: str(row.warning),
    warning_unit: str(row.warning_unit),
    tsn: unknown ? "" : str(row.total_life_used),
    tsn_unknown: unknown,
    tso: str(row.life_at_install),
    aircraft_install: str(row.aircraft_install),
    install_date: isoDay(row.install_date),
    expiry_date: isoDay(row.expiry_date),
    limitation_notes: str(row.limitation_notes),
    time_to_run: str(row.units_remaining),
    reference: str(row.reference),
    position: str(row.position),
  };
}

const numOrNull = (v: string) => (v.trim() === "" ? null : v.trim());

/** The POST / PATCH body. Off an aircraft the install fields are not sent (the API ignores
 * them there anyway); a blank Due Off on the date axis lets the API compute legacy's. */
export function limitationPayload(
  values: LimitationFormValues,
  fitted: boolean,
  rules: TypeRules = { noPeriod: false, noLife: false }
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    based_on: values.based_on.trim(),
    limitation_type: values.limitation_type || null,
    life: numOrNull(limitedLifePeriod(values, rules)) ?? "0",
    life_hours: numOrNull(values.life_hours) ?? "0",
    warning: numOrNull(values.warning) ?? "0",
    warning_unit: values.warning_unit || null,
    tso: numOrNull(values.tso) ?? "0",
    limitation_notes: values.limitation_notes || null,
    reference: values.reference.trim() || null,
  };
  if (fitted) body.position = values.position.trim() || null;
  if (values.tsn_unknown) body.tsn_unknown = true;
  else body.tsn = numOrNull(values.tsn) ?? "0";
  if (isDateAxis(values.based_on)) body.expiry_date = values.expiry_date || null;
  if (fitted) {
    body.install_date = values.install_date || null;
    if (!isDateAxis(values.based_on)) body.aircraft_install = numOrNull(values.aircraft_install);
  } else if (numOrNull(values.time_to_run) !== null) {
    body.time_to_run = values.time_to_run.trim();
  }
  return body;
}

/** The Period a save sends. Limited Life: the period IS the finite life (EDIT:583-586) — unless no
 * finite life is entered, when the stored period stands rather than be wiped to 0. */
export function limitedLifePeriod(values: LimitationFormValues, rules: TypeRules): string {
  return rules.noPeriod && Number(values.life_hours) > 0 ? values.life_hours : values.life;
}

/** TSO can never exceed TSN: time since overhaul counts from an overhaul inside the life since
 * new. A known TSN (0 or more; null = UNK) caps the TSO. Aaron 2026-10-13, "Add the guard" (C-GQZV
 * D7: planet gears seeded TSO 2100 over TSN 0); the API refuses the same (app/items/limitations.py
 * tso_over_tsn). Legacy has no such check. */
export function tsoOverTsn(tsn: number | null, tso: number | null): string | null {
  if (tsn === null || tso === null || !Number.isFinite(tsn) || !Number.isFinite(tso)) return null;
  if (tsn < 0 || tso <= tsn) return null;
  return `TSO at install (${tso}) cannot be more than TSN at install (${tsn})`;
}

/** The form's own checks, before a request: what the API would refuse with a 422. */
export function limitationFormErrors(values: LimitationFormValues, fitted: boolean): Partial<Record<keyof LimitationFormValues, string>> {
  const errors: Partial<Record<keyof LimitationFormValues, string>> = {};
  if (!values.based_on.trim()) errors.based_on = "Based On is required";
  if (fitted && !values.install_date) errors.install_date = "Date On is required";
  if (fitted && !isDateAxis(values.based_on) && !values.aircraft_install.trim()) {
    errors.aircraft_install = "@A/C Unit Fitted is required";
  }
  // as limitationPayload sends them: a blank figure is 0
  const tsoOver = tsoOverTsn(
    values.tsn_unknown ? null : Number(numOrNull(values.tsn) ?? "0"),
    Number(numOrNull(values.tso) ?? "0")
  );
  if (tsoOver) errors.tso = tsoOver;
  return errors;
}

/** A config value in whichever shape /v6/config serves it — a comma STRING, an ARRAY, or for a
 * "k:v" list an OBJECT (its keys) — as trimmed, non-empty strings. */
export function configList(raw: unknown): string[] {
  let items: unknown[] = [];
  if (typeof raw === "string") items = raw.split(",");
  else if (Array.isArray(raw)) items = raw;
  else if (raw && typeof raw === "object") items = Object.keys(raw as Record<string, unknown>);
  return items.map((item) => String(item).trim()).filter(Boolean);
}

const unique = (values: string[]) => Array.from(new Set(values.filter(Boolean)));

/** Based On choices: the tenant's aircraft_based_on_list (legacy's dropdown, ACAPI:2093-2112), then
 * any value already on the item (custom axes like "HOOK TIME") and the row's own. A tenant with no
 * list keeps the shared vocabulary. */
export function basedOnChoices(tenantList: unknown, known: string[] = [], current = ""): string[] {
  const tenant = configList(tenantList);
  return unique([...(tenant.length ? tenant : [...BASED_ON_OPTIONS]), ...known, current.trim()]);
}

/** The Type select's options, keeping a stored type the tenant's list does not name (it would show
 * blank, and Mustang has 417 such rows: Retirement, Inspection, Replacement, …). */
export function typeChoices(raw: unknown, current = ""): Array<{ value: string; label: string }> {
  const options = componentTypeOptions(raw);
  const value = current.trim();
  return value && !options.some((o) => o.value === value) ? [...options, { value, label: value }] : options;
}

export interface TypeRules {
  /** aircraft_limitation_type_no_period (Limited Life): Period = Finite Life, TSO = TSN. */
  noPeriod: boolean;
  /** aircraft_limitation_type_no_life (Overhaul): no Finite Life. */
  noLife: boolean;
}

/** The type's rules from the tenant's config (served prefix-stripped), legacy's defaults
 * "Limited Life" / "Overhaul" when unset (aircraft_config.inc:20, :33). */
export function typeRules(type: string, aircraftConfig?: Record<string, unknown> | null): TypeRules {
  const noPeriod = configList(aircraftConfig?.limitation_type_no_period);
  const noLife = configList(aircraftConfig?.limitation_type_no_life);
  const t = type.trim();
  return {
    noPeriod: Boolean(t) && (noPeriod.length ? noPeriod : ["Limited Life"]).includes(t),
    noLife: Boolean(t) && (noLife.length ? noLife : ["Overhaul"]).includes(t),
  };
}

/** Legacy's default Warning Unit for a Based On (aircraft.inc:2310-2318, its JS fallback; the
 * per-model aircraft_model_based_on_types unit is not served). null = leave the unit as it is. */
export function defaultWarningUnit(basedOn: string): string | null {
  for (const unit of ["Days", "Months", "Years", "Hours", "Cycles", "RINS", "Events", "Starts", "Landings"]) {
    if (basedOn.includes(unit)) return unit;
  }
  return null;
}
